import empty from "is-empty";
import { cloneDeep } from "lodash";

import { Tx } from "./types";
import {
    FETCH_STATES,
    REQUEST_STATES,
    TX_STATES,
    LOAN_TYPES,
} from "./constants";
import { MD } from "src/redux/MarketData/types";
import * as Validators from "src/utils/validators";
import {
    qtyByCountry,
    getTermFromDate,
    getDefaultTxPurpose,
    getClampedMaturityDate,
    getDateFromTerm,
    getMaximumPossibleMaturityDate,
} from "src/utils";
import Math from "src/utils/math";
import { ROLE } from "src/redux/TopBar/constants";
import {
    INTERMEDIARY_SELECT_TYPES,
    TX_TYPE_VALUES,
} from "src/redux/Confirmations/constants";
import { SettingsSelectors } from "src/redux/Settings/selectors";

const defaultInitiate = {
    selectedCptys: [],
    createRowData: [],
    status: REQUEST_STATES.NONE,
    errorMessage: "",
    errors: [],
    disables: [],
    comment: "",
    offerSettings: {
        type: LOAN_TYPES.LOAN,
        objective: "",
    },
    requestSettings: {
        intermediary: INTERMEDIARY_SELECT_TYPES.ANY,
        callable: "",
        collateralType: {
            type: "",
            cash: {},
            nonCash: { schedule: "" },
            ccy: [],
        },
        txType: TX_TYPE_VALUES.ANY, //Any
        error: "",
        loanType: LOAN_TYPES.LOAN,
    },
    purpose: "",
};

const defaultRespond = {
    comment: "",
    status: REQUEST_STATES.NONE,
    errorMessage: "",
};

const defaultState = {
    initiate: cloneDeep(defaultInitiate),
    respond: cloneDeep(defaultRespond),
    txBorrowEntries: [],
    txLendEntries: [],
    hideInactive: false,
    selectedRowIds: [],
    selectedSecurity: null,
    hasActionableEA: null,
    selectedRowStatus: null,
};

export const mapLendEntry = (entry, idx) => {
    return {
        ...entry,
        originRequestedEconomics: cloneDeep(entry.requestedEconomics),
        originOfferedEconomics: cloneDeep(entry.offeredEconomics),
        originFR: cloneDeep(entry.fr),
        idx,
    };
};

export const mapBorrowEntry = (entry, idx) => {
    return {
        ...entry,
        originRequestedEconomics: cloneDeep(entry.requestedEconomics),
        originOfferedEconomics: cloneDeep(entry.offeredEconomics),
        oldFundName: entry.fundName,
        idx,
    };
};

const compareTxs = (txA, txB) => {
    let out = txA.lastUpdated - txB.lastUpdated;
    if (out === 0) {
        out = txA.security.name < txB.security.name ? -1 : 1;
    }
    if (out === 0) {
        out = txA.id < txB.id ? -1 : 1;
    }
    return out;
};

const attachTruQty = (offerEntry, inventory) => {
    const ticker = offerEntry.marketData?.data?.ticker;
    if (!!ticker && !!inventory[ticker] && !empty(inventory[ticker])) {
        const matchedInventory = inventory[ticker].find(
            (i) => i.listName === offerEntry.ListName
        );
        if (matchedInventory) {
            return { ...offerEntry, TruQuantity: matchedInventory.truQuantity };
        }
    }
    return offerEntry;
};

const isSame = (existingTx, newTx) => {
    if (existingTx && newTx) {
        return (
            existingTx.state === newTx.state &&
            existingTx.comment === newTx.comment &&
            existingTx.offeredEconomics === newTx.offeredEconomics &&
            existingTx.lastUser === newTx.lastUser
        );
    }
    return false;
};

const updateTxEntryState = (e, action, mapFunction) => {
    // NOTE: if action does not have state, don't check tx equivalence
    if (!action.state || !isSame(e, action.tx)) {
        //Worth making a change, as it's not the same
        let comment = e.comment;
        if (action.tx && action.tx.comment) {
            comment = action.tx.comment;
        }
        if (action.comment) {
            comment = action.comment;
        }
        let offeredEconomics =
            action.tx?.offeredEconomics || e.offeredEconomics;
        if (
            action.state === TX_STATES.REJECTED &&
            !empty(e.requestedEconomics)
        ) {
            //When Lender rejects Borrower's request
            offeredEconomics = e.originOfferedEconomics || {
                dividend: offeredEconomics?.dividend,
                callable: offeredEconomics?.callable,
            };
        }
        return mapFunction({
            ...e,
            state: action.state || action.tx.state,
            prevState: e.state,
            type: action.tx?.type || e.type,
            objective: action.tx?.objective || e.objective,
            newType: action.tx?.type,
            originType: action.tx?.originType || e.originType,
            originObjective: action.tx?.originObjective || e.originObjective,
            lastUpdated: action.tx?.lastUpdated || e.lastUpdated,
            expiresAt: action.tx?.expiresAt || e.expiresAt,
            lastUser: action.tx?.lastUser || e.lastUser,
            listName: action.tx?.listName || e.listName,
            fundName: action.tx?.fundName || e.fundName,
            fr: action.tx?.fr || e.fr,
            comment: comment,
            orderId: action.tx?.orderId || e.orderId,
            purpose: action.tx?.purpose || e.purpose,
            requestedEconomics:
                action.tx?.requestedEconomics || e.requestedEconomics,
            eaUpgradeRequestExpireAt: action.tx?.eaUpgradeRequestExpireAt,
            offeredEconomics,
        });
    }
    return e;
};

export const updateLendEntries = (txLendEntries, action) => {
    //Apparently, using map like this is the most efficient way to update. It's nice that it preserves order without re-sorting
    return txLendEntries.map((e) => {
        if (e.id === action.domainId) {
            //this is the tx we're looking for
            return updateTxEntryState(e, action, mapLendEntry);
        }
        return e; //leave the existing one
    });
};

export const updateBorrowerEntries = (txBorrowEntries, action) => {
    return txBorrowEntries.map((e) => {
        if (e.id === action.domainId) {
            //this is the tx we're looking for
            return updateTxEntryState(e, action, mapBorrowEntry);
        }
        return e; //leave the exisint one
    });
};

const updateTxStateList = (state, action) => {
    //Apparently, using map like this is the most efficient way to update. It's nice that it preserves order without re-sorting

    let lendEntries = state.txLendEntries.map((e) => {
        let tx = action.txEntries.find((t) => t.domainId === e.id);
        if (tx) {
            //this is the tx we're looking for
            return updateTxEntryState(
                e,
                { tx: tx.txEntry || tx, state: tx.txEntry?.state || tx.state },
                mapLendEntry
            );
        }
        return e; //leave the existing one
    });

    let borrowEntries = state.txBorrowEntries.map((e) => {
        let tx = action.txEntries.find((t) => t.domainId === e.id);
        if (tx) {
            //this is the tx we're looking for
            return updateTxEntryState(
                e,
                { tx: tx.txEntry || tx, state: tx.state },
                mapBorrowEntry
            );
        }
        return e; //leave the existing one
    });
    return {
        ...state,
        txBorrowEntries: borrowEntries,
        txLendEntries: lendEntries,
    };
};

export const selectRows = (role, txEntries, prevIds, newIds) => {
    let edited = false;
    // refactored to map to prevent A direct mutation error as map return a new array
    const updatedEntries = txEntries.map((entry) => {
        // Deep copy the entry to avoid direct mutation
        let newEntry = {
            ...cloneDeep(entry),
            offeredEconomics: cloneDeep(entry.offeredEconomics),
            requestedEconomics: cloneDeep(entry.requestedEconomics),
            originOfferedEconomics: cloneDeep(entry.originOfferedEconomics),
            originRequestedEconomics: cloneDeep(entry.originRequestedEconomics),
        };

        if (role === ROLE.LENDER && newEntry.keepOffer) {
            delete newEntry.keepOffer;
            edited = true;
        } else {
            const deselected =
                prevIds.includes(entry.id) && !newIds.includes(entry.id);
            const selected =
                !prevIds.includes(entry.id) && newIds.includes(entry.id);
            if (!deselected && !selected) return entry; // Return original entry if no changes

            if (
                deselected &&
                role === ROLE.BORROWER &&
                entry.state === TX_STATES.OFFERED
            ) {
                // Restore old fund name from the preloan request that's associated with this offer (if any)
                newEntry.fundName = entry.oldFundName;

                // Restore offered economics
                newEntry.offeredEconomics = entry.originOfferedEconomics;

                // Remove purpose (todo: what if this was an offer against a locate with pre-existing purpose..?)
                delete newEntry.purpose;

                edited = true;

                return newEntry;
            }

            const updateEconomics =
                role === ROLE.LENDER
                    ? entry.state === TX_STATES.PRE_LOAN_REQUESTED
                    : entry.state === TX_STATES.FORKED;

            if (!updateEconomics) return entry; // Return original entry if no changes

            if (deselected) {
                // Deselect
                if (role === ROLE.LENDER) {
                    newEntry.offeredEconomics = {
                        ...entry.originOfferedEconomics,
                    };
                    newEntry.fr = entry.originFR;
                    delete newEntry.listName;
                } else {
                    // Borrower deselected
                    newEntry.requestedEconomics = {
                        ...entry.originRequestedEconomics,
                    };
                }
            } else {
                // Select
                if (role === ROLE.LENDER) {
                    let reqDividend = entry.requestedEconomics?.dividend;
                    newEntry.offeredEconomics = {
                        ...entry.requestedEconomics,
                        dividend: reqDividend === undefined ? 100 : reqDividend,
                    };
                } else {
                    newEntry.requestedEconomics.fee = {
                        ...entry.offeredEconomics.fee,
                    };
                }
            }

            edited = true;
        }
        return newEntry;
    });

    return edited
        ? role === ROLE.LENDER
            ? { txLendEntries: updatedEntries }
            : { txBorrowEntries: updatedEntries }
        : {};
};

export default (state = defaultState, action) => {
    let verifyResult;

    switch (action.type) {
        /**
         * TX_INITIATE
         */
        case Tx.SET_HIDE_INACTIVE:
            return { ...state, hideInactive: action.hideInactive };

        case Tx.SET_INITIATE_CPTY:
            let initiate = {
                ...state.initiate,
                selectedCptys: action.selectedCptys,
            };
            if (
                state.initiate.cptyError === false ||
                state.initiate.cptyError === true
            ) {
                // If this value is undefined, we don't need to set it
                // This filed will be set after 'submit' button is clicked
                initiate.cptyError = empty(action.selectedCptys);
            }

            return {
                ...state,
                initiate,
            };

        case Tx.SET_INITIATE_COMMENT:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    comment: action.comment,
                },
            };
        case Tx.UPDATE_AC:
            verifyResult = Validators.verifyAcceptanceCriteria(
                action.includesAC,
                action.ac,
                action.rowData
            );

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    errors: verifyResult.errors,
                    disables: verifyResult.disables,
                },
            };

        case Tx.SET_OFFER_COMMENT:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    comment: action.comment,
                },
            };

        case Tx.SET_OFFER_SETTINGS:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    errors: Validators.verifyOfferSettings(action.settings),
                    offerSettings: action.settings,
                },
            };

        case Tx.SET_REQUEST_SETTINGS:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    requestSettings: action.settings,
                },
            };
        case Tx.RESET_INIT:
            return {
                ...state,
                initiate: cloneDeep(defaultInitiate),
            };

        case Tx.PREPARE_INIT:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: action.rowData.map((row, rowIdx) => ({
                        ...row,
                        id: rowIdx + 1,
                    })),
                },
            };

        case Tx.ATTACH_INVENTORY:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: state.initiate.createRowData.map((row) =>
                        attachTruQty(row, action.inventory)
                    ),
                },
            };

        case Tx.UPDATE_INIT_ROW:
            const ids = action.rows.map((r) => r.id);
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: state.initiate.createRowData.map((r) => {
                        const position = ids.indexOf(r.id);
                        if (position >= 0) return action.rows[position];
                        return r;
                    }),
                },
            };

        case Tx.DELETE_INIT_ROW:
            let newInitRows4 = [...state.initiate.createRowData];
            newInitRows4.splice(action.rowId - 1, 1);

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: newInitRows4.map((row, idx) => ({
                        ...row,
                        id: idx + 1,
                    })),
                },
            };

        case Tx.SEND_INIT:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    status: REQUEST_STATES.SENDING,
                    errorMessage: "",
                },
            };

        case Tx.INIT_ERROR:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    status: REQUEST_STATES.ERROR,
                    errorMessage: action.errorMessage,
                },
            };

        case Tx.INIT_SENT:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    status: REQUEST_STATES.SENT,
                    errorMessage: "",
                },
            };

        case Tx.CANCEL_SEND:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    status: REQUEST_STATES.NONE,
                },
            };
        case Tx.UPDATE_TX_STATE:
            if (action.role === ROLE.LENDER) {
                return {
                    ...state,
                    txLendEntries: action.txEntries,
                };
            }
            return {
                ...state,
                txBorrowEntries: action.txEntries,
            };
        case Tx.UPDATE_TX_STATE_LIST:
            return updateTxStateList(state, action);
        case Tx.INSERT_BORROW_TX_LIST:
            return {
                ...state,
                txBorrowEntries: state.txBorrowEntries
                    .filter((e) => !e?.id?.startsWith("temp"))
                    .concat(action.txSet.map(mapBorrowEntry))
                    .sort(compareTxs),
            };
        case Tx.INSERT_LEND_TX_LIST:
            const newIds = action.txEntries.map((e) => e.id);
            return {
                ...state,
                txLendEntries: state.txLendEntries
                    .filter(
                        (e) =>
                            !newIds.includes(e.id) && !e.id.startsWith("temp")
                    )
                    .concat(action.txEntries.map(mapLendEntry))
                    .sort(compareTxs),
            };
        case Tx.UPDATE_TX_BORROW_ENTRIES:
            return { ...state, txBorrowEntries: action.txBorrowEntries };
        case Tx.UPDATE_TX_LEND_ENTRIES:
            return { ...state, txLendEntries: action.txLendEntries };
        case Tx.GET_MARKET_DATA:
            let hasMarkitAccess = false;
            let txInitRows = state.initiate.createRowData.map((row, index) => {
                const stockSymbol = row.SecurityId;
                const batchStockData = action.result;

                if (!action.listSecurityId.includes(stockSymbol)) return row;
                let stockItem = batchStockData[stockSymbol]
                    ? batchStockData[stockSymbol].data
                    : null;

                if (stockItem) {
                    const quantity = qtyByCountry(
                        row.Quantity,
                        stockItem.countryCode,
                        action.role === ROLE.BORROWER
                    );
                    const benchmarkFee = stockItem.referenceRate?.markit?.value;
                    const utilization = stockItem.utilization?.markit?.value;
                    if (index === 0) {
                        hasMarkitAccess = !empty(stockItem.referenceRate);
                    }

                    const defaultPurpose =
                        action.role === ROLE.BORROWER
                            ? getDefaultTxPurpose(stockItem)
                            : null;

                    const TradeableDates = stockItem.tradableDates.map((s) =>
                        s.replace(/-/g, "")
                    );
                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: stockItem,
                            error: null,
                        },
                        security: stockItem,
                        hasError: false,
                        Name: stockItem.name,
                        BbgCode: stockItem.bbgCode,
                        LocalName: stockItem.localName,
                        PreviousClose: stockItem.closingPrice,
                        Amount: stockItem.closingPrice * quantity,
                        Country: stockItem.countryCode,
                        TradeableDates,
                        Quantity: quantity,
                        Utilization: utilization,
                        BenchmarkFee: benchmarkFee,
                        CalculatedRate: row.Rate,
                        Purpose: defaultPurpose,
                        maxMaturityDates: stockItem.maxMaturityDates,
                        Term: getTermFromDate(row.ValueDate, row.Maturity),
                    };
                }
                return {
                    ...row,
                    marketData: {
                        status: FETCH_STATES.ERROR,
                        error: "Not Found",
                    },
                    SecurityId: row.SecurityId + " ",
                    hasError: true,
                    Name: "ERROR",
                    LocalName: "ERROR",
                    PreviousClose: "ERROR",
                    Amount: "ERROR",
                    Country: "ERROR",
                };
            });

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    hasMarkitAccess,
                    createRowData: txInitRows,
                },
            };
        case Tx.APPLY_REF_RATE:
            const rowData = state.initiate.createRowData.map((d) => ({
                ...d,
                CalculatedRate: d.BenchmarkFee
                    ? Math.chain(d.BenchmarkFee)
                          .divide(100)
                          .multiply(action.multiplier)
                          .done()
                    : d.CalculatedRate,
            }));

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: rowData,
                },
            };

        case Tx.APPLY_MATURITY:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: state.initiate.createRowData.map((row) => {
                        if (action.maturity.type === "MAX") {
                            const maxMaturity = getMaximumPossibleMaturityDate(
                                row.Country,
                                row.Purpose,
                                row.ValueDate,
                                row.TradeableDates,
                                row.maxMaturityDates,
                                action.enable90DayMaxMaturity
                            );
                            return {
                                ...row,
                                Maturity: new Date(maxMaturity),
                                Term: getTermFromDate(
                                    row.ValueDate,
                                    maxMaturity
                                ),
                            };
                        }

                        return {
                            ...row,
                            Maturity: row.ValueDate
                                ? action.maturity.date ||
                                  getDateFromTerm(
                                      row.ValueDate,
                                      action.maturity.term
                                  )
                                : new Date("9999-12-31"),
                            Term: row.ValueDate
                                ? action.maturity.term ||
                                  getTermFromDate(
                                      row.ValueDate,
                                      action.maturity.date
                                  )
                                : "",
                        };
                    }),
                },
            };

        case Tx.APPLY_SEND_OFFER_DETAILS:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: state.initiate.createRowData.map((row) => ({
                        ...row,
                        Callable:
                            action.details.Callable === ""
                                ? row.originListDetails.Callable
                                : action.details.Callable,
                        CollateralType:
                            action.details.CollateralType ||
                            row.originListDetails.CollateralType,
                        CollateralCCY:
                            action.details.CollateralCCY ||
                            row.originListDetails.CollateralCCY,
                        Dividend:
                            action.details.Dividend ||
                            row.originListDetails.Dividend,
                        FR:
                            (action.details.CollateralType ||
                                row.originListDetails.CollateralType) === "CA"
                                ? "R"
                                : "F",
                    })),
                },
            };

        case Tx.APPLY_TX_TYPE:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    requestSettings: {
                        ...state.initiate.requestSettings,
                        txType: action.txType,
                    },
                },
            };
        case Tx.APPLY_INTERMEDIARY:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    requestSettings: {
                        ...state.initiate.requestSettings,
                        intermediary: action.intermediary,
                    },
                },
            };

        case Tx.EDIT_MARKET_DATA:
            let txMdRowData = [];
            state.initiate.createRowData.forEach((row) => {
                if (row.SecurityId === action.stockSymbol) {
                    const quantity = qtyByCountry(
                        row.Quantity,
                        action.result.data.countryCode,
                        true
                    );
                    txMdRowData.push({
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: action.result.data,
                            id: action.result.id,
                            error: null,
                        },
                        security: action.result.data,
                        Name: action.result.data.name,
                        LocalName: action.result.data.localName,
                        PreviousClose: action.result.data.closingPrice,
                        Amount: action.result.data.closingPrice * quantity,
                        Country: action.result.data.countryCode,
                        TradeableDates: action.result.data.tradableDates.map(
                            (s) => s.replace(/-/g, "")
                        ),
                        Quantity: quantity,
                    });
                } else {
                    txMdRowData.push(row);
                }
            });
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: txMdRowData,
                },
            };

        case Tx.REPORT_MD_ERROR:
            let newInitRows3 = [...state.initiate.createRowData];
            let rowToEdit3 = newInitRows3[action.rowId - 1];

            newInitRows3.splice(action.rowId - 1, 1, {
                ...rowToEdit3,
                marketData: {
                    status: FETCH_STATES.ERROR,
                    error: action.error,
                },
                SecurityId: rowToEdit3.SecurityId + " ",
                hasError: true,
                Name: "ERROR",
                LocalName: "ERROR",
                PreviousClose: "ERROR",
                Amount: "ERROR",
                Country: "ERROR",
            });

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: newInitRows3,
                },
            };

        /**
         * TX_LIST
         */
        case Tx.UPDATE_BORROW_LIST_UI_EDIT: {
            const ids = action.entries.map((e) => e.id);
            const txBorrowEntries = state.txBorrowEntries.map((e) => {
                const index = ids.indexOf(e.id);
                if (index >= 0) return action.entries[index];
                return e;
            });
            return { ...state, txBorrowEntries };
        }

        case Tx.UPDATE_LEND_LIST_UI_EDIT: {
            const ids = action.entries.map((e) => e.id);
            const txLendEntries = state.txLendEntries.map((e) => {
                const index = ids.indexOf(e.id);
                if (index >= 0) return action.entries[index];
                return e;
            });
            return {
                ...state,
                txLendEntries,
            };
        }

        case Tx.UPDATE_BORROW_LIST:
            return {
                ...state,
                txBorrowEntries: action.txEntries
                    .map(mapBorrowEntry)
                    .sort(compareTxs),
            };

        case Tx.UPDATE_LEND_LIST:
            return {
                ...state,
                txLendEntries: action.txEntries
                    .map(mapLendEntry)
                    .sort(compareTxs),
            };

        /**
         * TX_RESPOND
         */
        case Tx.SET_RESPOND_COMMENT:
            return {
                ...state,
                respond: {
                    ...state.respond,
                    comment: action.comment,
                },
            };

        case Tx.SEND_RESPOND:
            return {
                ...state,
                respond: {
                    ...state.respond,
                    status: REQUEST_STATES.SENDING,
                    errorMessage: "",
                },
            };
        case Tx.RESPOND_ERROR:
            return {
                ...state,
                respond: {
                    ...state.respond,
                    status: REQUEST_STATES.ERROR,
                    errorMessage: action.errorMessage,
                },
            };

        case Tx.RESPOND_SENT:
            return {
                ...state,
                respond: {
                    ...state.respond,
                    status: REQUEST_STATES.SENT,
                    errorMessage: "",
                },
            };

        case Tx.SET_CPTY_ERROR:
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    cptyError: action.value,
                },
            };

        case MD.MARKET_DATA_UPDATE:
            let mdRowData = [];
            state.initiate.createRowData.forEach((row) => {
                if (row.SecurityId === action.stockSymbol) {
                    mdRowData.push({
                        ...row,
                        marketData: {
                            status: FETCH_STATES.FETCHED,
                            data: action.result.data,
                            id: action.result.id,
                            error: null,
                        },
                        Name: action.result.data.name,
                        LocalName: action.result.data.localName,
                        PreviousClose: action.result.data.closingPrice,
                        Amount: action.result.data.closingPrice * row.Quantity,
                        Country: action.result.data.countryCode,
                    });
                } else {
                    mdRowData.push(row);
                }
            });
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: mdRowData,
                },
            };

        case MD.MARKET_DATA_BATCH_UPDATE:
            const BATCH_SIZE = 200;
            const batchStart = action.batchNo * BATCH_SIZE;
            const batchEnd = batchStart + BATCH_SIZE;
            const batchStockSymbols = action.batchStockSymbols;
            const batchStockData = action.result;

            // find a matching stock item for each ticker within the same batch range
            let batchRowData = state.initiate.createRowData
                .slice(batchStart, batchEnd)
                .map((row, i) => {
                    const stockSymbol = batchStockSymbols[i];
                    let stockItem = batchStockData[stockSymbol]
                        ? batchStockData[stockSymbol].data
                        : null;

                    if (stockItem) {
                        return {
                            ...row,
                            marketData: {
                                status: FETCH_STATES.FETCHED,
                                data: stockItem,
                                error: null,
                            },
                            hasError: false,
                            Name: stockItem.name,
                            LocalName: stockItem.localName,
                            PreviousClose: stockItem.closingPrice,
                            Amount: stockItem.closingPrice * row.Quantity,
                            Country: stockItem.countryCode,
                        };
                    }

                    return {
                        ...row,
                        marketData: {
                            status: FETCH_STATES.ERROR,
                            error: "Not Found",
                        },
                        // if the cell value doesnt change then cellClass isnt
                        // updated, so we add a space
                        SecurityId: row.SecurityId + " ",
                        hasError: true,
                        Name: "ERROR",
                        LocalName: "ERROR",
                        PreviousClose: "ERROR",
                        Amount: -1,
                    };
                });

            // replace placeholders with complete market data
            let newCreateRowData = state.initiate.createRowData;
            newCreateRowData.splice(
                batchStart,
                batchRowData.length,
                ...batchRowData
            );

            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: newCreateRowData,
                },
            };

        case MD.MD_ERROR:
            let mdRowErrorData = [];
            state.initiate.createRowData.forEach((row) => {
                if (row.SecurityId === action.stockSymbol) {
                    mdRowErrorData.push({
                        ...row,
                        marketData: {
                            status: FETCH_STATES.ERROR,
                            error: action.error,
                        },
                        // if the cell value doesnt change then cellClass isnt
                        // updated, so we add a space
                        SecurityId: row.SecurityId + " ",
                        Name: "ERROR",
                        LocalName: "ERROR",
                        PreviousClose: "ERROR",
                        Amount: -1,
                    });
                } else {
                    mdRowErrorData.push(row);
                }
            });
            return {
                ...state,
                initiate: {
                    ...state.initiate,
                    createRowData: mdRowErrorData,
                },
            };
        case Tx.SET_SELECTED_SECURITY:
            return {
                ...state,
                selectedSecurity: action.security,
            };
        case Tx.SET_SELECTED_ROWS:
            if (!action.updateOfferedEconomics) {
                return {
                    ...state,
                    selectedRowIds: action.rowIds || [],
                };
            } else {
                const txLendEntries = [...state.txLendEntries];
                const txBorrowEntries = [...state.txBorrowEntries];
                const selectedRowIds = [...state.selectedRowIds];
                const txList = selectRows(
                    action.role,
                    action.role === ROLE.LENDER
                        ? txLendEntries
                        : txBorrowEntries,
                    selectedRowIds,
                    action.rowIds
                );
                return {
                    ...state,
                    ...txList,
                    selectedRowIds: action.rowIds || [],
                };
            }
        case Tx.SET_SELECTED_ROW_INFO:
            return {
                ...state,
                hasActionableEA: action.value,
                selectedRowStatus: action.status,
            };
        default:
            return state;
    }
};
